import Subtitle from "../subtitle/subtitle";
import { useContext, useEffect, useState } from "react";
import { StepperDataContext } from "../../context/stepper-context";
import SlotCard from "./slot-card/slot-card";
import {
  getMoreAppointmentsSlots,
  submitTimeSlotsRequest,
} from "../../../../actions";
import { SelectedPet } from "../pet-details/types";
import { AxiosError } from "axios";
import { useAppDispatch } from "../../../../store/hooks";
import { addNotification } from "../../../../store/slices/ui-slice";
import { v4 as uuid } from "uuid";
import { SlotCardStyled } from "./slot-card/slot-card.styles";
import { LightButton } from "../../../../components/button/button";
import { BookSoltsDetailsStyled, BaseCards } from "./book-slot.styles";
import { TimeSlots } from "./types";

const BookSlot: React.FC = () => {
  const dispatch = useAppDispatch();
  const [cache, setCache] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const {
    selectedClient,
    selectedPatients,
    selectedTimeSlot,
    selectedProvider,
  } = useContext(StepperDataContext);

  const [timeSlots, setTimeSlots] = useState<TimeSlots>([]);

  useEffect(() => {
    const payload = {
      client: {
        id: selectedClient?.id,
        integration_id: selectedClient?.integration_id,
      },
      patients: selectedPatients.map((p: SelectedPet) => {
        return {
          integration_id: p.integration_id,
          clientReason: p.reason?.toLowerCase(),
          appointmentType: {
            integration_id: p.appointment_type,
          },
        };
      }),
      provider: selectedProvider?.actor_id,
    };

    setIsLoading(true);
    submitTimeSlotsRequest(payload)
      .then((response) => {
        const { timeSlots, cacheKey } = response.data.data;
        setTimeSlots(
          Array.isArray(timeSlots) ? timeSlots : Object.values(timeSlots)
        );
        if (timeSlots.length === 0) {
          dispatch(
            addNotification({
              id: uuid(),
              type: "warning",
              message: "No available time slots.",
            })
          );
        }
        setCache(cacheKey);
      })
      .catch((error: AxiosError) => {
        dispatch(
          addNotification({
            id: uuid(),
            type: "error",
            message: error.message,
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, selectedClient?.id, selectedClient?.integration_id, selectedPatients, selectedProvider?.actor_id]);

  useEffect(() => {
    if (selectedTimeSlot && selectedTimeSlot.isManuallySelected) {
      setTimeSlots((timeSlots: TimeSlots) => {
        if (
          timeSlots.find(
            (x) =>
              x.block.start === selectedTimeSlot.start &&
              x.block.end === selectedTimeSlot.end
          )
        ) {
          return timeSlots;
        } else {
          return [
            ...timeSlots,
            {
              block: {
                start: selectedTimeSlot.start,
                end: selectedTimeSlot.end,
              },
            },
          ];
        }
      });
    }
  }, [selectedTimeSlot]);

  const handleMore = () => {
    setIsLoading(true);
    getMoreAppointmentsSlots({ cacheKey: cache })
      .then((response) => {
        const { timeSlots, cacheKey } = response.data.data;
        const newEntries: TimeSlots = Object.values(timeSlots);
        setTimeSlots((timeSlots) => [...timeSlots, ...newEntries]);
        if (newEntries.length === 0) {
          dispatch(
            addNotification({
              id: uuid(),
              type: "warning",
              message: "No more available time slots.",
            })
          );
        }
        if (cacheKey !== null && cacheKey !== cache) {
          console.error("Cache key mismatch");
        }
        setCache(cacheKey); // Disable the load more button
      })
      .catch((error: AxiosError) => {
        dispatch(
          addNotification({
            id: uuid(),
            type: "error",
            message: error.message,
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    return () => setCache(""); // Invalidate the cacheKey
  }, []);

  return (
    <BookSoltsDetailsStyled>
      <div className="pet-details-header">
        <Subtitle title="Book time slot" />
      </div>
      <BaseCards>
        {timeSlots.map((d, index) => (
          <SlotCard key={index} slot={d} />
        ))}
        <SlotCardStyled>
          <LightButton
            className="load-more"
            disabled={!cache || isLoading || timeSlots?.length === 0}
            onClick={handleMore}
            isLoading={isLoading}
          >
            Load more
          </LightButton>
        </SlotCardStyled>
      </BaseCards>
    </BookSoltsDetailsStyled>
  );
};

export default BookSlot;
